import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';

/**
 * Spec 229 — the small-screen nav drawer.
 *
 * An overlay must be dismissible, so this closes on the trigger, on Escape, on
 * a click outside the panel, and on navigating to a new route (otherwise
 * tapping a nav item leaves the drawer covering the page you just asked for).
 * Focus returns to the trigger on close, or the keyboard user is stranded at
 * the top of the document.
 */
export function useDrawer() {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const { pathname } = useLocation();

  const close = useCallback(() => {
    setOpen((wasOpen) => {
      if (wasOpen) triggerRef.current?.focus();
      return false;
    });
  }, []);

  const toggle = useCallback(() => setOpen((v) => !v), []);

  // Route change closes it. Deliberately does NOT restore focus: the user is
  // navigating, so focus belongs to the new page, not back on the trigger.
  useEffect(() => { setOpen(false); }, [pathname]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      close();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, close]);

  // Move focus into the panel when it opens so the keyboard lands where the
  // eye does.
  useEffect(() => {
    if (open) panelRef.current?.querySelector<HTMLElement>('a, button')?.focus();
  }, [open]);

  return { open, toggle, close, panelRef, triggerRef };
}
